import { receiptPaymentMethods } from '@billing/validation/receipt';
import {
  CLAIM_PAYMENT_ATTACHMENT_CONTENT_TYPES,
  claimPaymentAttachmentContentTypeSchema,
  type ClaimPaymentAttachmentUploadTarget,
} from '@billing/validation/attachment';
import { type RecordClaimPaymentInput } from '@billing/validation/claim';
import {
  Button,
  Chip,
  ChipGroup,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  NumberInput,
  useComputedColorScheme,
  useMantineTheme,
  Flex,
  ActionIcon,
  Alert,
  Paper,
} from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import { DatePickerInput } from '@mantine/dates';
import { useHotkeys } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { type Invoice } from '@billing/validation/invoice';
import { claimQueryKeys } from 'api/resource';
import { usePrepareClaimPaymentAttachments } from 'api/resource/claimAttachment';
import { useTranslation } from '@refinedev/core';
import {
  IconAlertTriangle,
  IconFile,
  IconPaperclip,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';

import React, { useEffect, useMemo, useState } from 'react';
import { buttonTextColor } from 'utils/buttonUtils';
import { normalizePaymentDate, nowISODate } from 'utils/dateUtils';
import { uploadFileToSignedURL } from 'utils/fileUpload';

interface MarkAsPaidModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: (input: RecordClaimPaymentInput) => void | Promise<void>;
  invoice: Invoice | null;
  claimID: string;
  tariffs: Array<{
    id: string;
    code: string;
    requestedAmount?: string | number | null;
  }>;
  title?: string;
  body?: string;
  emphasizePhrase?: string;
}

type SelectedAttachment = {
  id: string;
  file: FileWithPath;
};

type AttachmentUploadStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';

export const MarkAsPaidModal: React.FC<MarkAsPaidModalProps> = ({
  open,
  setOpen,
  onConfirm,
  invoice,
  claimID,
  tariffs,
  title = 'Mark Paid',
  body = 'Are you sure you want to mark this claim as Paid?',
  emphasizePhrase = 'Paid',
}) => {
  const { translate } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: prepareClaimPaymentAttachments } =
    usePrepareClaimPaymentAttachments();
  const mode = useComputedColorScheme();
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<receiptPaymentMethods>(
    receiptPaymentMethods.CREDIT
  );
  const [paymentDate, setPaymentDate] = useState<string | null>(nowISODate());
  const [files, setFiles] = useState<SelectedAttachment[]>([]);
  const [creditID, setCreditID] = useState<string | null>(null);
  const [uploadTargets, setUploadTargets] = useState<
    ClaimPaymentAttachmentUploadTarget[]
  >([]);
  const [uploadStatus, setUploadStatus] = useState<
    Record<string, AttachmentUploadStatus>
  >({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialTariffAmounts = useMemo(
    () =>
      tariffs.reduce((acc, tariff) => {
        const requestedAmount = Number(tariff.requestedAmount ?? 0);
        acc[tariff.id] = Number.isFinite(requestedAmount) ? requestedAmount : 0;
        return acc;
      }, {} as Record<string, number>),
    [tariffs]
  );

  const [tariffAmounts, setTariffAmounts] =
    useState<Record<string, number>>(initialTariffAmounts);

  useEffect(() => {
    if (open) {
      setComment('');
      setTariffAmounts(initialTariffAmounts);
    }
  }, [initialTariffAmounts, open]);

  const highlightedBody = useMemo(() => {
    if (!emphasizePhrase) return body;
    const phrase = emphasizePhrase.trim();
    if (!phrase) return body;

    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = body.split(regex);
    if (parts.length === 1) return body;

    const isDark = mode === 'dark';
    const palette = theme.colors[theme.primaryColor] || [];
    const shade = isDark ? palette[4] : palette[6];

    return parts.map((part, idx) =>
      regex.test(part) ? (
        <Text key={idx} span fw={700} fs="italic" c={shade}>
          {part}
        </Text>
      ) : (
        <Text key={idx} span>
          {part}
        </Text>
      )
    );
  }, [body, emphasizePhrase, mode, theme]);

  const handleClose = () => {
    setOpen(false);
    setComment('');
    setTariffAmounts(initialTariffAmounts);
    setPaymentMethod(receiptPaymentMethods.CREDIT);
    setPaymentDate(nowISODate());
    setFiles([]);
    setCreditID(null);
    setUploadTargets([]);
    setUploadStatus({});
    setUploadProgress({});
    setSubmitError(null);
  };

  const resetPreparedUploads = () => {
    setCreditID(null);
    setUploadTargets([]);
    setUploadStatus({});
    setUploadProgress({});
    setSubmitError(null);
  };

  const handleDrop = (acceptedFiles: FileWithPath[]) => {
    const remaining = Math.max(0, 5 - files.length);
    if (acceptedFiles.length > remaining) {
      setSubmitError(
        translate(
          'pages:claims.markPaid.attachments.maxFiles',
          'A maximum of five files can be attached.'
        )
      );
    }
    const additions = acceptedFiles.slice(0, remaining).map((file) => ({
      id: uuidv4(),
      file,
    }));
    if (!additions.length) return;
    setFiles((current) => [...current, ...additions]);
    resetPreparedUploads();
  };

  const handleRemoveFile = (id: string) => {
    setFiles((current) => current.filter((item) => item.id !== id));
    resetPreparedUploads();
  };

  const setAttachmentUploadStatus = (
    attachmentID: string,
    status: AttachmentUploadStatus
  ) =>
    setUploadStatus((current) => ({
      ...current,
      [attachmentID]: status,
    }));

  const prepareAttachmentUploadTargets = async (): Promise<
    ClaimPaymentAttachmentUploadTarget[]
  > => {
    if (uploadTargets.length === files.length) return uploadTargets;

    const targets = await prepareClaimPaymentAttachments({
      claimID,
      files: files.map(({ file }) => ({
        originalFilename: file.name,
        contentType: claimPaymentAttachmentContentTypeSchema.parse(file.type),
        sizeBytes: file.size,
      })),
    });
    setUploadTargets(targets);
    setUploadStatus(
      Object.fromEntries(targets.map((target) => [target.id, 'pending']))
    );
    return targets;
  };

  const uploadPreparedAttachments = async (
    targets: ClaimPaymentAttachmentUploadTarget[]
  ): Promise<void> => {
    for (const [index, target] of targets.entries()) {
      if (uploadStatus[target.id] === 'uploaded') continue;

      const selectedFile = files[index]?.file;
      if (!selectedFile) {
        throw new Error('Selected attachment is unavailable.');
      }

      setAttachmentUploadStatus(target.id, 'uploading');
      try {
        await uploadFileToSignedURL({
          signedURL: target.uploadURL,
          data: selectedFile,
          contentType: target.contentType,
          onProgress: (value) =>
            setUploadProgress((current) => ({
              ...current,
              [target.id]: value,
            })),
        });
        setAttachmentUploadStatus(target.id, 'uploaded');
      } catch (error) {
        setAttachmentUploadStatus(target.id, 'failed');
        throw error;
      }
    }
  };

  const buildPaymentInput = (
    paymentCreditID: string,
    targets: ClaimPaymentAttachmentUploadTarget[]
  ): RecordClaimPaymentInput => ({
    claimID,
    creditID: paymentCreditID,
    paymentDate: normalizePaymentDate(paymentDate),
    paymentMethod,
    comment: comment.trim(),
    perTariffAmounts: Object.fromEntries(
      Object.entries(tariffAmounts).map(([tariffID, amount]) => [
        tariffID,
        amount.toFixed(2),
      ])
    ),
    invoiceID: invoice?.id ?? null,
    attachments: targets.map(
      ({ id, originalFilename, contentType, sizeBytes }) => ({
        id,
        originalFilename,
        contentType: claimPaymentAttachmentContentTypeSchema.parse(contentType),
        sizeBytes,
      })
    ),
  });

  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);
    setSubmitError(null);
    try {
      const nextCreditID = creditID ?? uuidv4();
      if (!creditID) setCreditID(nextCreditID);

      const targets =
        files.length > 0
          ? await prepareAttachmentUploadTargets()
          : uploadTargets;
      if (targets.length > 0) await uploadPreparedAttachments(targets);
      const paymentInput = buildPaymentInput(nextCreditID, targets);
      await Promise.resolve(onConfirm(paymentInput));
      await queryClient.invalidateQueries({
        queryKey: claimQueryKeys.all,
      });
      handleClose();
    } catch {
      setSubmitError(
        translate(
          'pages:claims.markPaid.submitError',
          'Unable to record the payment. Your selections have been kept so you can retry.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // Keyboard shortcut Cmd/Ctrl+Enter using Mantine useHotkeys
  useHotkeys([
    [
      'mod+Enter',
      () => {
        if (open) {
          handleConfirm();
        }
      },
    ],
  ]);

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      closeOnEscape
      withCloseButton
      size="lg"
      centered
      radius="lg"
      shadow="xl"
      overlayProps={{ blur: 3, opacity: 0.5 }}
      title={<Text fw={700}>{title}</Text>}
      styles={{
        content: {
          background:
            mode === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          border:
            mode === 'dark'
              ? `1px solid ${theme.colors.dark[4]}`
              : `1px solid ${theme.colors.gray[3]}`,
        },
        header: { paddingBottom: 0 },
        title: {
          color: mode === 'dark' ? theme.white : undefined,
        },
        close: {
          border: `1px solid ${
            mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
          }`,
          background: mode === 'dark' ? theme.colors.dark[6] : theme.white,
          borderRadius: 10,
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${
              mode === 'dark' ? theme.colors.blue[6] : theme.colors.blue[5]
            }`,
          },
        },
      }}
    >
      <Stack gap="md" align="stretch">
        <Text size="sm" ta="center">
          {highlightedBody}
        </Text>
        <Stack gap="xs">
          {tariffs.map((tariff) => (
            <NumberInput
              key={tariff.id}
              label={translate('pages:claims.markPaid.amountByTariffLabel', {
                tariffCode: tariff.code,
              })}
              placeholder={translate('pages:claims.markPaid.amountPlaceholder')}
              value={tariffAmounts[tariff.id]}
              onChange={(value: number | string) => {
                setTariffAmounts((current) => ({
                  ...current,
                  [tariff.id]: typeof value === 'number' ? value : 0,
                }));
              }}
              min={0}
              step={0.01}
              hideControls
            />
          ))}
        </Stack>
        <Flex direction="row" gap="xs">
          <ChipGroup
            value={paymentMethod}
            onChange={(val) => setPaymentMethod(val as receiptPaymentMethods)}
          >
            <Chip variant="outline" value={receiptPaymentMethods.CREDIT}>
              CREDIT
            </Chip>
            <Chip variant="outline" value={receiptPaymentMethods.ETRANSFER}>
              ETRANSFER
            </Chip>
            <Chip variant="outline" value={receiptPaymentMethods.CHEQUE}>
              CHEQUE
            </Chip>
            <Chip variant="outline" value={receiptPaymentMethods.CASH}>
              CASH
            </Chip>
          </ChipGroup>
        </Flex>
        <DatePickerInput
          value={paymentDate}
          label={translate('pages:claims.markPaid.paymentDateLabel')}
          placeholder={translate(
            'pages:claims.markPaid.paymentDatePlaceholder'
          )}
          onChange={setPaymentDate}
          clearable={false}
          valueFormat="YYYY-MM-DD"
        />
        <Paper withBorder radius="md" p="sm">
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {translate(
                'pages:claims.markPaid.attachments.label',
                'Payment attachments (optional)'
              )}
            </Text>
            <Dropzone
              onDrop={handleDrop}
              onReject={(rejections) => {
                const exceedsMaximum = files.length + rejections.length > 5;
                setSubmitError(
                  exceedsMaximum
                    ? translate(
                        'pages:claims.markPaid.attachments.maxFiles',
                        'A maximum of five files can be attached.'
                      )
                    : translate(
                        'pages:claims.markPaid.attachments.invalidType',
                        'Only PDF, JPEG, and PNG files can be attached.'
                      )
                );
              }}
              accept={[...CLAIM_PAYMENT_ATTACHMENT_CONTENT_TYPES]}
              maxFiles={Math.max(1, 5 - files.length)}
              multiple
              disabled={loading || files.length >= 5}
              data-testid="mark-paid-attachment-dropzone"
            >
              <Group justify="center" gap="xs" mih={72}>
                <Dropzone.Accept>
                  <IconUpload size={22} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={22} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconPaperclip size={22} />
                </Dropzone.Idle>
                <Text size="sm">
                  {translate(
                    'pages:claims.markPaid.attachments.dropzone',
                    'Attach up to five PDF, JPEG, or PNG files'
                  )}
                </Text>
              </Group>
            </Dropzone>
            {files.map(({ id, file }, index) => {
              const targetID = uploadTargets[index]?.id;
              const status = targetID ? uploadStatus[targetID] : undefined;
              const progress = targetID ? uploadProgress[targetID] : undefined;
              return (
                <Group key={id} justify="space-between" gap="xs">
                  <Group gap="xs" style={{ minWidth: 0 }}>
                    <IconFile size={16} />
                    <Text size="sm" truncate>
                      {file.name}
                    </Text>
                    {status && (
                      <Text size="xs" c="dimmed">
                        {status === 'uploading'
                          ? `${status} ${progress ?? 0}%`
                          : status}
                      </Text>
                    )}
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => handleRemoveFile(id)}
                    disabled={loading}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Group>
              );
            })}
          </Stack>
        </Paper>
        <Textarea
          minRows={8}
          autosize={false}
          placeholder="Enter your internal comment here..."
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              handleConfirm();
            }
          }}
          styles={(theme) => ({
            input: {
              background: mode === 'dark' ? theme.colors.dark[8] : undefined,
              borderColor:
                mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
              minHeight: 180,
              width: '100%',
            },
          })}
          w="100%"
        />
        {submitError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {submitError}
          </Alert>
        )}
        <Group justify="center" mt="sm" gap="lg">
          <Button
            color="gray"
            c={buttonTextColor(mode, false)}
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="green"
            c={buttonTextColor(mode, false)}
            onClick={handleConfirm}
            loading={loading}
          >
            Confirm
          </Button>
        </Group>
        <Text size="xs" c="dimmed">
          Cmd/Ctrl + Enter to confirm
        </Text>
      </Stack>
    </Modal>
  );
};
